import { memo, useCallback, useId, useMemo, useState } from 'react';
import { cn, RichTextInput, RichTextInputProps, TableListFieldItemValue } from '@page/ui';

import { InlineEditor } from '@page/core/components/editor/InlineEditor';

/**
 * Trims empty content from rich text markdown.
 * Removes:
 * - Trailing empty lines
 * - Any empty todolist items (- [ ] or - [x] with no text)
 * - Any empty list items (- or * with no text)
 * - Any empty ordered list items (1. with no text)
 * Normalizes:
 * - Multiple spaces after [ ] or [x] to a single space
 */
const trimRichTextValue = (value: string): string => {
  if (!value) return value;
  const lines = value.split('\n');

  const filtered = lines.filter((line) => {
    const trimmed = line.trim();
    if (/^[-*]\s*\[[x\s]\]\s*$/i.test(trimmed)) return false;
    if (/^[-*]\s*$/.test(trimmed)) return false;
    if (/^\d+[.)]\s*$/.test(trimmed)) return false;
    return true;
  });

  // Remove trailing empty lines
  while (filtered.length > 0 && filtered[filtered.length - 1].trim() === '') {
    filtered.pop();
  }

  // Normalize multiple spaces before checkbox brackets to a single space
  const normalized = filtered.map((line) => line.replace(/([-*])\s{2,}(\[[x\s]\])/gi, '$1 $2'));

  return normalized.join('\n');
};

export interface RichTextFieldProps {
  value: string;
  placeholder?: string;
  actions?: React.ReactNode;
  editActions?: React.ReactNode;
  alwaysDisplayActions?: boolean;
  className?: string;
  onSave?: (value: string) => void;
  onEdit?: () => void;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  toolbar?: RichTextInputProps['toolbar'];
  headingConfig?: RichTextInputProps['headingConfig'];
  defaultBlockType?: RichTextInputProps['defaultBlockType'];
  clickToEdit?: boolean;
  /** Minimum number of visible text lines. */
  minLines?: number;
}

const RichTextFieldBase = ({
  value,
  placeholder,
  actions,
  alwaysDisplayActions = false,
  editActions,
  className,
  onSave,
  onEdit,
  isEditing: externalIsEditing,
  setIsEditing: externalSetIsEditing,
  toolbar,
  headingConfig,
  defaultBlockType,
  clickToEdit = true,
  minLines = 1,
}: RichTextFieldProps) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [forceHidePlaceholder, setForceHidePlaceholder] = useState(false);
  const actionClassName = useId();
  const clickOutsideExcludeClassNames = useMemo(() => [actionClassName], [actionClassName]);

  const isControlled = externalIsEditing !== undefined;
  const isEditing = isControlled ? externalIsEditing : internalIsEditing;
  const setIsEditing = isControlled ? externalSetIsEditing : setInternalIsEditing;

  const handleEdit = useCallback(() => {
    if (isEditing) return;
    setEditValue(value);
    setIsEditing?.(true);
    onEdit?.();
  }, [value, isEditing, setIsEditing, onEdit]);

  const handleSave = useCallback(() => {
    setIsEditing?.(false);

    const trimmedValue = trimRichTextValue(editValue);

    if (trimmedValue === trimRichTextValue(value)) return;

    onSave?.(trimmedValue);
  }, [editValue, value, setIsEditing, onSave]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      handleSave();
    }
  };

  const handleDataChange = (newValue: string) => {
    setEditValue(newValue);

    if (!value && !newValue) {
      setForceHidePlaceholder((prev) => !prev);
    }
  };

  const renderEditMode = () => (
    <div onKeyDown={handleInputKeyDown}>
      <RichTextInput
        containerClassName="min-h-full static px-3 py-3"
        size="md"
        value={editValue}
        onChange={handleDataChange}
        onClickOutside={handleSave}
        clickOutsideExcludeClassNames={clickOutsideExcludeClassNames}
        autofocus
        toolbar={toolbar}
        headingConfig={headingConfig}
        defaultBlockType={defaultBlockType}
        placeholder={forceHidePlaceholder ? undefined : placeholder}
        placeholderClassName="left-3 translate-y-0 top-3"
        minLines={minLines}
      />
    </div>
  );

  const renderViewMode = () => (
    <>
      <div className="normalized-markdown">
        <InlineEditor data={value} readonly minLines={minLines} />
      </div>

      {!value && placeholder && (
        <div className="text-text-tertiary font-body-md absolute top-3 left-3">{placeholder}</div>
      )}
    </>
  );

  return (
    <TableListFieldItemValue
      actions={actions}
      alwaysDisplayActions={alwaysDisplayActions || isEditing}
      editActions={editActions}
      actionsClassName={actionClassName}
      className={cn(
        {
          'p-0': isEditing,
          'text-text-disabled': !value,
        },
        className,
      )}
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      renderEditMode={renderEditMode}
      // already handle from the RichTextInput
      saveOnClickOutside={false}
      clickToEdit={clickToEdit}
    >
      {renderViewMode()}
    </TableListFieldItemValue>
  );
};

export const RichTextField = memo(RichTextFieldBase);
